import { useEffect, useMemo, useState } from "react";
import { Drawer, Modal, Select, message } from "antd";
import GooglePlacesAutocompleteInput from "../../common/GooglePlacesAutocompleteInput";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import useAppConfig from "../../hooks/access-control/useAppConfig";
import { careService } from "../../services/careService";

const CLOCK_REASONS = [
  "Hospital visit",
  "Medical appointment",
  "Dental appointment",
  "Walk",
  "Family visit",
  "Community outing",
  "Adult day program",
  "Church or spiritual activity",
  "Shopping",
  "Emergency",
  "Other",
];

const asList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.content)) return value.content;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  return [];
};

const firstText = (...values) => values.find((value) => value !== null && value !== undefined && value !== "") || "";
const compactObject = (value = {}) =>
  Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined && entry !== null && entry !== ""));

const fullNameFor = (person = {}) =>
  firstText(
    person.name,
    person.fullName,
    person.residentName,
    person.displayName,
    [person.firstName, person.middleName, person.lastName].filter(Boolean).join(" "),
    person.email,
    "Resident",
  );

const personDisplayName = (person = {}) =>
  firstText(
    person.name,
    person.fullName,
    person.displayName,
    [person.firstName, person.middleName, person.lastName].filter(Boolean).join(" "),
    person.email,
    "Care giver",
  );

const dateTimeLocalValue = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const isoFromDateTimeLocal = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString();
};

const formatDateTime = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};

const dateMs = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.getTime();
};

const formatDuration = (milliseconds) => {
  if (!Number.isFinite(milliseconds)) return "Not set";
  const totalMinutes = Math.max(0, Math.round(Math.abs(milliseconds) / 60000));
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  const parts = [];
  if (days) parts.push(`${days} day${days === 1 ? "" : "s"}`);
  if (hours) parts.push(`${hours} hr${hours === 1 ? "" : "s"}`);
  if (minutes || !parts.length) parts.push(`${minutes} min`);
  return parts.slice(0, 2).join(" ");
};

const durationBetween = (start, end) => {
  const startMs = dateMs(start);
  const endMs = dateMs(end);
  if (startMs === null || endMs === null || endMs < startMs) return "Not set";
  return formatDuration(endMs - startMs);
};

const returnTimingLabel = (expected, actual) => {
  const expectedMs = dateMs(expected);
  const actualMs = dateMs(actual);
  if (expectedMs === null || actualMs === null) return "Not set";
  const diff = actualMs - expectedMs;
  if (Math.abs(diff) < 60000) return "On time";
  return `${formatDuration(diff)} ${diff > 0 ? "late" : "early"}`;
};

const returnTimingTone = (expected, actual) => {
  const expectedMs = dateMs(expected);
  const actualMs = dateMs(actual);
  if (expectedMs === null || actualMs === null) return "";
  const diff = actualMs - expectedMs;
  if (Math.abs(diff) < 60000) return "on-time";
  return diff > 0 ? "late" : "early";
};

const residentIdFor = (resident = {}) => firstText(resident.id, resident.residentId, resident.patientId);
const recordResidentId = (record = {}) => firstText(record.residentId, record.resident?.id, record.patientId);
const recordResidentName = (record = {}, residents = []) =>
  firstText(record.residentName, record.resident?.name, fullNameFor(residents.find((resident) => residentIdFor(resident) === recordResidentId(record))), "Resident");
const recordResidentDob = (record = {}, residents = []) =>
  firstText(record.resident?.dob, record.resident?.dateOfBirth, residents.find((resident) => residentIdFor(resident) === recordResidentId(record))?.dob);
const recordClockOutTime = (record = {}) =>
  firstText(record.clockOutTime, record.clockOutAt, record.clockOutTimestamp, record.outTime, record.leftAt, record.startTime, record.createdAt);
const recordExpectedBackTime = (record = {}) =>
  firstText(record.expectedBackTime, record.expectedBackAt, record.expectedReturnTime, record.expectedReturnAt, record.returnExpectedAt);
const recordClockInTime = (record = {}) =>
  firstText(record.clockInTime, record.clockInAt, record.clockInTimestamp, record.inTime, record.returnedAt, record.endTime);
const recordDestinationAddress = (record = {}) =>
  firstText(record.destinationLocationAddress, record.destinationAddress, record.destination, record.locationAddress, record.location);
const recordEmergencyContactPhone = (record = {}) =>
  firstText(record.emergencyContactPhone, record.emergencyPhone, record.contactPhone, record.emergencyContact?.phone);
const isClockedOut = (record = {}) => Boolean(recordClockOutTime(record)) && !recordClockInTime(record);

const staffIdFor = (staff = {}) =>
  firstText(
    staff.careProfileId,
    staff.caregiverProfileId,
    staff.careGiverProfileId,
    staff.profileId,
    staff.caregiverUserId,
    staff.careGiverUserId,
    staff.userProfileId,
    staff.caregiverId,
    staff.careGiverId,
    staff.userProfile?.id,
    staff.user?.id,
    staff.caregiver?.id,
    staff.careGiver?.id,
    staff.id,
    staff.email,
    staff.caregiverEmail,
  );
const staffNameFor = (staff = {}) =>
  firstText(
    staff.name,
    staff.caregiverName,
    staff.careGiverName,
    staff.careProfileName,
    staff.staffName,
    staff.userProfile?.name,
    staff.user?.name,
    staff.caregiver?.name,
    staff.careGiver?.name,
    [staff.firstName, staff.middleName, staff.lastName].filter(Boolean).join(" "),
    [staff.userProfile?.firstName, staff.userProfile?.middleName, staff.userProfile?.lastName].filter(Boolean).join(" "),
    [staff.user?.firstName, staff.user?.middleName, staff.user?.lastName].filter(Boolean).join(" "),
    [staff.caregiver?.firstName, staff.caregiver?.middleName, staff.caregiver?.lastName].filter(Boolean).join(" "),
    [staff.careGiver?.firstName, staff.careGiver?.middleName, staff.careGiver?.lastName].filter(Boolean).join(" "),
    staff.email,
    "Care staff",
  );
const staffEmailFor = (staff = {}) =>
  firstText(staff.email, staff.caregiverEmail, staff.careGiverEmail, staff.primaryEmail, staff.workEmail, staff.userProfile?.email, staff.user?.email, staff.caregiver?.email, staff.careGiver?.email);
const staffRoleFor = (staff = {}) => firstText(staff.role, staff.assignmentRole, staff.relationship, staff.contactType, "Care Team");
const staffSnapshot = (staff = {}) => ({
  staffId: staffIdFor(staff),
  name: staffNameFor(staff),
  email: staffEmailFor(staff),
  role: staffRoleFor(staff),
});
const clockRecordStaffList = (record = {}) =>
  asList(record.accompanyingStaff || record.accompanyingStaffMembers || record.staff || record.staffMembers);
const clockRecordStaffIds = (record = {}) =>
  asList(record.accompanyingStaffIds || record.staffIds || record.accompanyingCareStaffIds || record.careStaffIds);
const recordStaffText = (record = {}) => {
  const staff = clockRecordStaffList(record);
  if (staff.length) {
    return staff
      .map((member) => {
        if (typeof member === "string") return member;
        return [member.name || member.staffName || member.caregiverName || member.careGiverName, member.email || member.caregiverEmail || member.careGiverEmail]
          .filter(Boolean)
          .join(" - ");
      })
      .filter(Boolean)
      .join(", ");
  }
  const names = asList(record.accompanyingStaffNames || record.staffNames || record.accompanyingCareStaffNames);
  if (names.length) return names.join(", ");
  const ids = clockRecordStaffIds(record);
  return ids.length ? ids.join(", ") : "Not added";
};
const blankForm = () => ({
  residentId: "",
  reason: "",
  otherReason: "",
  description: "",
  clockOutTime: dateTimeLocalValue(),
  expectedBackTime: "",
  destinationLocationAddress: "",
  emergencyContactPhone: "",
  staffIds: [],
});

const blankCheckInForm = () => ({
  clockInTime: dateTimeLocalValue(),
  comments: "",
});

export default function ResidentClockInOutPage() {
  const { user } = useAuth();
  const { selectedWorkspaceId } = useWorkspace();
  const { appConfig, loading: appConfigLoading } = useAppConfig();
  const googleMapsApiKey =
    appConfig?.googleMapsApiKey ||
    appConfig?.googleMapsApiMatrixKey ||
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    process.env.VITE_GOOGLE_MAPS_API_KEY ||
    "";
  const [residents, setResidents] = useState([]);
  const [records, setRecords] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [staffLoading, setStaffLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [checkingInId, setCheckingInId] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [checkInTarget, setCheckInTarget] = useState(null);
  const [checkInForm, setCheckInForm] = useState(blankCheckInForm);
  const [form, setForm] = useState(blankForm);
  const [error, setError] = useState("");
  const [residentFilterId, setResidentFilterId] = useState("");
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [recordsRefreshKey, setRecordsRefreshKey] = useState(0);

  const actorId = firstText(user?.id, user?.userId, user?.uid);
  const actorName = personDisplayName(user);
  const workspaceParams = useMemo(() => compactObject({ workspaceId: selectedWorkspaceId, wid: selectedWorkspaceId }), [selectedWorkspaceId]);
  const selectedResident = residents.find((resident) => residentIdFor(resident) === form.residentId) || null;
  const staffChoices = staffOptions
    .map((staff) => ({ ...staff, _clockStaffId: staffIdFor(staff) }))
    .filter((staff, index, list) => staff._clockStaffId && list.findIndex((item) => item._clockStaffId === staff._clockStaffId) === index);
  const selectedStaffIds = form.staffIds;
  const selectedStaff = staffChoices.filter((staff) => selectedStaffIds.includes(staff._clockStaffId));
  const selectedReason = form.reason === "Other" ? form.otherReason : form.reason;
  const selectedResidentOpenRecord = useMemo(
    () => records.find((record) => recordResidentId(record) === form.residentId && isClockedOut(record)) || null,
    [form.residentId, records],
  );

  useEffect(() => {
    let active = true;
    setError("");
    careService.residents.listMini({ ...workspaceParams, size: 100 })
      .then((result) => {
        if (!active) return;
        setResidents(asList(result));
      })
      .catch((err) => {
        if (active && err?.response?.status !== 404) setError(err?.response?.data?.message || err?.message || "Unable to load residents.");
      });
    return () => {
      active = false;
    };
  }, [workspaceParams]);

  useEffect(() => {
    setPage(0);
  }, [rangeStart, rangeEnd, residentFilterId]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    careService.residentClockInOut.page(compactObject({
      ...workspaceParams,
      residentId: residentFilterId,
      startDate: rangeStart,
      endDate: rangeEnd,
      page,
      size: pageSize,
      sort: "clockOutTime,desc",
    }))
      .then((result) => {
        if (!active) return;
        const content = asList(result);
        const nextTotalPages = Math.max(1, Number(result?.totalPages) || 1);
        if (page >= nextTotalPages) {
          setPage(nextTotalPages - 1);
          return;
        }
        setRecords(content);
        setTotalElements(Number(result?.totalElements) || 0);
        setTotalPages(nextTotalPages);
      })
      .catch((err) => {
        if (!active) return;
        setRecords([]);
        setTotalElements(0);
        setTotalPages(1);
        setError(err?.response?.data?.message || err?.message || "Unable to load resident CheckInOut records.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [page, pageSize, rangeEnd, rangeStart, recordsRefreshKey, residentFilterId, workspaceParams]);

  useEffect(() => {
    if (!drawerOpen || !form.residentId) {
      setStaffOptions([]);
      return undefined;
    }
    let active = true;
    setStaffLoading(true);
    Promise.allSettled([
      careService.careStaffAssignments.list({ ...workspaceParams, residentId: form.residentId, date: new Date().toISOString().slice(0, 10) }),
      careService.careProfiles.list({ ...workspaceParams, size: 100 }),
    ])
      .then(([assignmentsResult, profilesResult]) => {
        if (!active) return;
        const assignments = assignmentsResult.status === "fulfilled" ? asList(assignmentsResult.value) : [];
        const profiles = profilesResult.status === "fulfilled" ? asList(profilesResult.value) : [];
        setStaffOptions(assignments.length ? assignments : profiles);
        const error = [assignmentsResult, profilesResult]
          .find((result) => result.status === "rejected" && result.reason?.response?.status !== 404)?.reason;
        if (!assignments.length && !profiles.length && error) {
          message.error(error?.response?.data?.message || error?.message || "Unable to load accompanying staff.");
        }
      })
      .finally(() => {
        if (active) setStaffLoading(false);
      });
    return () => {
      active = false;
    };
  }, [drawerOpen, form.residentId, workspaceParams]);

  const updateForm = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const openClockOut = () => {
    setForm(blankForm());
    setDrawerOpen(true);
  };
  const closeDrawer = () => {
    if (saving) return;
    setDrawerOpen(false);
  };
  const openCheckInConfirmation = (record) => {
    if (!record?.id || !isClockedOut(record)) return;
    setCheckInTarget(record);
    setCheckInForm(blankCheckInForm());
  };
  const closeCheckInConfirmation = () => {
    if (checkingInId) return;
    setCheckInTarget(null);
    setCheckInForm(blankCheckInForm());
  };
  const updateCheckInForm = (field, value) => setCheckInForm((current) => ({ ...current, [field]: value }));
  const submitCheckIn = async (event) => {
    event.preventDefault();
    if (!checkInTarget?.id || !isClockedOut(checkInTarget)) return;
    if (!checkInForm.clockInTime) {
      message.error("System check-in time is required.");
      return;
    }
    const residentName = recordResidentName(checkInTarget, residents);
    const comments = String(checkInForm.comments || "").trim();
    const payload = compactObject({
      residentId: recordResidentId(checkInTarget),
      workspaceId: selectedWorkspaceId,
      clockInTime: isoFromDateTimeLocal(checkInForm.clockInTime),
      description: comments,
      clockInDescription: comments,
      updatedByUserId: actorId,
      updatedByName: actorName,
      status: "CLOCKED_IN",
    });
    setCheckingInId(checkInTarget.id);
    try {
      const saved = await careService.residentClockInOut.clockIn(checkInTarget.id, payload, workspaceParams);
      const checkedInRecord = saved && typeof saved === "object" ? saved : { ...checkInTarget, ...payload };
      setRecords((current) => [checkedInRecord, ...current.filter((item) => item.id !== checkInTarget.id)]);
      setSelectedRecord((current) => (current?.id === checkInTarget.id ? checkedInRecord : current));
      setCheckInTarget(null);
      setCheckInForm(blankCheckInForm());
      setRecordsRefreshKey((current) => current + 1);
      message.success(`${residentName} checked in.`);
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || "Unable to check resident in.");
    } finally {
      setCheckingInId("");
    }
  };
  const saveClockOut = async (event) => {
    event.preventDefault();
    if (!form.residentId) {
      message.error("Select a resident before checking out.");
      return;
    }
    if (!String(selectedReason || "").trim()) {
      message.error("Select or enter a reason before checking out.");
      return;
    }
    if (!form.clockOutTime) {
      message.error("Check-out time is required.");
      return;
    }
    if (selectedResidentOpenRecord) {
      message.error(`${recordResidentName(selectedResidentOpenRecord, residents)} is already checked out. Check the resident in before checking out again.`);
      return;
    }
    setSaving(true);
    const payload = compactObject({
      residentId: form.residentId,
      residentName: fullNameFor(selectedResident || {}),
      workspaceId: selectedWorkspaceId,
      reason: String(selectedReason).trim(),
      description: String(form.description || "").trim(),
      clockOutTime: isoFromDateTimeLocal(form.clockOutTime),
      expectedBackTime: isoFromDateTimeLocal(form.expectedBackTime),
      destinationLocationAddress: String(form.destinationLocationAddress || "").trim(),
      emergencyContactPhone: String(form.emergencyContactPhone || "").trim(),
      accompanyingStaffIds: selectedStaffIds,
      accompanyingStaff: selectedStaff.map(staffSnapshot),
      createdByUserId: actorId,
      createdByName: actorName,
      status: "CLOCKED_OUT",
    });
    try {
      await careService.residentClockInOut.clockOutForResident(form.residentId, payload, workspaceParams);
      setPage(0);
      setRecordsRefreshKey((current) => current + 1);
      setDrawerOpen(false);
      message.success(`${payload.residentName} checked out.`);
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || "Unable to check resident out.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <CareShell className="dashboard-content app-page-content resident-checkinout-page">
      <section className="care-page-hero">
        <div>
          <span>Resident CheckInOut log</span>
          <h1>Resident CheckInOut</h1>
          <p>Review resident check-out and check-in records across this workspace.</p>
        </div>
        <button className="mini-btn primary resident-checkinout-primary-action" type="button" onClick={openClockOut}>
          <i className="ti ti-clock-off" /> Check Out Resident
        </button>
      </section>

      {error ? <p className="form-error">{error}</p> : null}

      <Panel title="Resident CheckInOut">
        <div className="resident-checkinout-filters">
          <label>
            <span>Resident</span>
            <Select
              className="resident-checkinout-resident-filter"
              value={residentFilterId}
              onChange={setResidentFilterId}
              showSearch
              optionFilterProp="label"
              options={[
                { value: "", label: "All residents" },
                ...residents.map((resident) => ({ value: residentIdFor(resident), label: fullNameFor(resident) })),
              ]}
            />
          </label>
          <div className="resident-checkinout-filter-field">
            <span>Quick date</span>
            <button className="mini-btn" type="button" onClick={() => {
              const today = dateTimeLocalValue(new Date()).slice(0, 10);
              setRangeStart(today);
              setRangeEnd(today);
            }}>
              <i className="ti ti-calendar-event" /> Today
            </button>
          </div>
          <label>
            <span>From</span>
            <div className="resident-checkinout-filter-input">
              <i className="ti ti-calendar" />
              <input max={rangeEnd || undefined} type="date" value={rangeStart} onChange={(event) => setRangeStart(event.target.value)} />
            </div>
          </label>
          <label>
            <span>To</span>
            <div className="resident-checkinout-filter-input">
              <i className="ti ti-calendar" />
              <input min={rangeStart || undefined} type="date" value={rangeEnd} onChange={(event) => setRangeEnd(event.target.value)} />
            </div>
          </label>
          {(residentFilterId || rangeStart || rangeEnd) ? (
            <button className="mini-btn" type="button" onClick={() => {
              setResidentFilterId("");
              setRangeStart("");
              setRangeEnd("");
            }}>
              <i className="ti ti-filter-off" /> Clear
            </button>
          ) : null}
        </div>
        <div className="resident-tab-list" style={{ "--resident-record-columns": "minmax(220px, 1.4fr) 170px 170px 140px 120px" }}>
          <div className="resident-tab-list-row resident-tab-list-head-row">
            <span>Resident</span>
            <span>Check out</span>
            <span>Expected back</span>
            <span>Status</span>
            <span>Actions</span>
          </div>
          {records.map((record) => (
            <article
              className="resident-tab-list-row resident-checkinout-summary-row"
              key={record.id || `${recordResidentId(record)}-${recordClockOutTime(record)}`}
              role="button"
              tabIndex={0}
              onClick={() => setSelectedRecord(record)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setSelectedRecord(record);
                }
              }}
            >
              <span>
                <strong>{recordResidentName(record, residents)}</strong>
                <small>{record.reason || "No reason added"}</small>
              </span>
              <span>{formatDateTime(recordClockOutTime(record))}</span>
              <span>{formatDateTime(recordExpectedBackTime(record))}</span>
              <span><em className={`pill ${isClockedOut(record) ? "blue" : ""}`}>{isClockedOut(record) ? "Checked out" : "Checked in"}</em></span>
              <span className="resident-checkinout-row-actions">
                <button
                  className="mini-btn resident-checkinout-details-action"
                  type="button"
                  title="View details"
                  aria-label={`View details for ${recordResidentName(record, residents)}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    setSelectedRecord(record);
                  }}
                >
                  <i className="ti ti-eye" />
                </button>
                {isClockedOut(record) ? (
                  <button
                    className="mini-btn resident-checkinout-checkin-action"
                    type="button"
                    title="Check in"
                    aria-label={`Check in ${recordResidentName(record, residents)}`}
                    disabled={checkingInId === record.id}
                    onClick={(event) => {
                      event.stopPropagation();
                      openCheckInConfirmation(record);
                    }}
                  >
                    <i className={`ti ${checkingInId === record.id ? "ti-loader-2 spin" : "ti-clock-check"}`} />
                  </button>
                ) : null}
              </span>
            </article>
          ))}
          {loading ? <div className="resident-inline-empty">Loading resident CheckInOut records...</div> : null}
          {!loading && !records.length ? <div className="resident-inline-empty">No resident CheckInOut records found.</div> : null}
        </div>
        {!loading && totalElements ? (
          <div className="timesheet-pagination resident-checkinout-pagination">
            <span>
              Showing <strong>{(page * pageSize) + 1}-{Math.min(totalElements, (page + 1) * pageSize)}</strong> of <strong>{totalElements}</strong>
            </span>
            <label>
              Rows
              <select value={pageSize} onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(0);
              }}>
                {[10, 20, 50].map((size) => <option value={size} key={size}>{size}</option>)}
              </select>
            </label>
            <div className="timesheet-pagination-actions">
              <button className="mini-btn" type="button" onClick={() => setPage((current) => Math.max(0, current - 1))} disabled={page <= 0}>
                <i className="ti ti-chevron-left" /> Previous
              </button>
              <strong>Page {page + 1} of {totalPages}</strong>
              <button className="mini-btn" type="button" onClick={() => setPage((current) => Math.min(totalPages - 1, current + 1))} disabled={page >= totalPages - 1}>
                Next <i className="ti ti-chevron-right" />
              </button>
            </div>
          </div>
        ) : null}
      </Panel>

      <Drawer
        title="Resident CheckInOut Details"
        placement="right"
        open={Boolean(selectedRecord)}
        onClose={() => setSelectedRecord(null)}
        width={560}
        className="resident-action-drawer resident-checkinout-details-drawer"
        destroyOnClose
      >
        {selectedRecord ? (
          <div className="schedule-details-drawer">
            {isClockedOut(selectedRecord) ? (
              <div className="resident-checkinout-drawer-actions">
                <button
                  className="mini-btn primary resident-checkinout-checkin-command"
                  type="button"
                  disabled={checkingInId === selectedRecord.id}
                  onClick={() => openCheckInConfirmation(selectedRecord)}
                >
                  <i className={`ti ${checkingInId === selectedRecord.id ? "ti-loader-2 spin" : "ti-clock-check"}`} />
                  Check In Resident
                </button>
              </div>
            ) : null}

            <section>
              <span>Resident</span>
              <strong>{recordResidentName(selectedRecord, residents)}</strong>
              <small>
                {[recordResidentDob(selectedRecord, residents) ? `DOB ${recordResidentDob(selectedRecord, residents)}` : "", recordResidentId(selectedRecord)]
                  .filter(Boolean)
                  .join(" | ") || "Resident details not added"}
              </small>
            </section>

            <div className="schedule-editor-grid">
              <div>
                <span>Status</span>
                <strong>{isClockedOut(selectedRecord) ? "Checked out" : "Checked in"}</strong>
              </div>
              <div>
                <span>Reason</span>
                <strong>{selectedRecord.reason || "Not added"}</strong>
              </div>
            </div>

            <div className="schedule-editor-grid">
              <div>
                <span>Check out</span>
                <strong>{formatDateTime(recordClockOutTime(selectedRecord))}</strong>
              </div>
              <div>
                <span>Expected back</span>
                <strong>{formatDateTime(recordExpectedBackTime(selectedRecord))}</strong>
              </div>
            </div>

            <div className="schedule-editor-grid">
              <div>
                <span>Time out</span>
                <strong>{durationBetween(recordClockOutTime(selectedRecord), recordClockInTime(selectedRecord) || new Date().toISOString())}</strong>
              </div>
              <div>
                <span>Return timing</span>
                <strong className={`resident-checkinout-return-timing ${returnTimingTone(recordExpectedBackTime(selectedRecord), recordClockInTime(selectedRecord) || new Date().toISOString())}`}>
                  {returnTimingLabel(recordExpectedBackTime(selectedRecord), recordClockInTime(selectedRecord) || new Date().toISOString())}
                </strong>
              </div>
            </div>

            <section>
              <span>Check in</span>
              <strong>{formatDateTime(recordClockInTime(selectedRecord))}</strong>
            </section>

            <section>
              <span>Destination location address</span>
              <p>{recordDestinationAddress(selectedRecord) || "Not added"}</p>
            </section>

            <section>
              <span>Emergency contact phone</span>
              <p>{recordEmergencyContactPhone(selectedRecord) || "Not added"}</p>
            </section>

            <section>
              <span>Accompanying staff</span>
              <p>{recordStaffText(selectedRecord)}</p>
            </section>

            <section>
              <span>Clock-out description</span>
              <p>{selectedRecord.description || selectedRecord.clockOutDescription || "No details added."}</p>
            </section>

            <section>
              <span>Clock-in description</span>
              <p>{selectedRecord.clockInDescription || selectedRecord.checkInDescription || "No details added."}</p>
            </section>

            <div className="schedule-editor-grid">
              <div>
                <span>Created</span>
                <strong>{formatDateTime(selectedRecord.createdAt)}</strong>
              </div>
              <div>
                <span>Updated</span>
                <strong>{formatDateTime(selectedRecord.updatedAt)}</strong>
              </div>
            </div>
          </div>
        ) : null}
      </Drawer>

      <Modal
        title="Confirm Resident Check In"
        open={Boolean(checkInTarget)}
        onCancel={closeCheckInConfirmation}
        footer={null}
        destroyOnClose
        centered
        width={640}
        className="resident-checkinout-confirm-modal"
      >
        {checkInTarget ? (
          <form className="resident-checkinout-confirm-form" onSubmit={submitCheckIn}>
            <section className="resident-checkinout-confirm-summary">
              <i className="ti ti-clock-check" />
              <div>
                <span>Confirm return</span>
                <strong>{recordResidentName(checkInTarget, residents)}</strong>
                <small>
                  {[checkInTarget.reason, recordDestinationAddress(checkInTarget)].filter(Boolean).join(" - ") || "Resident CheckInOut record"}
                </small>
              </div>
            </section>
            <section className="resident-checkinout-confirm-panel">
              <div className="resident-checkinout-confirm-panel-head">
                <span>Return timing</span>
                <strong className={`resident-checkinout-return-timing ${returnTimingTone(recordExpectedBackTime(checkInTarget), isoFromDateTimeLocal(checkInForm.clockInTime))}`}>
                  {returnTimingLabel(recordExpectedBackTime(checkInTarget), isoFromDateTimeLocal(checkInForm.clockInTime))}
                </strong>
              </div>
              <div className="resident-checkinout-confirm-meta">
                <div className="resident-checkinout-confirm-time">
                  <span>Actual check-in time</span>
                  <strong>{formatDateTime(isoFromDateTimeLocal(checkInForm.clockInTime))}</strong>
                  <input
                    className="resident-checkinout-readonly-time"
                    type="datetime-local"
                    value={checkInForm.clockInTime}
                    readOnly
                    aria-readonly="true"
                    required
                  />
                </div>
                <div>
                  <span>Expected back</span>
                  <strong>{formatDateTime(recordExpectedBackTime(checkInTarget))}</strong>
                </div>
                <div>
                  <span>Checked out</span>
                  <strong>{formatDateTime(recordClockOutTime(checkInTarget))}</strong>
                </div>
                <div>
                  <span>Time out</span>
                  <strong>{durationBetween(recordClockOutTime(checkInTarget), isoFromDateTimeLocal(checkInForm.clockInTime))}</strong>
                </div>
              </div>
            </section>
            <label className="ncp-form-field resident-checkinout-confirm-comments">
              <span>Comments</span>
              <textarea
                value={checkInForm.comments}
                onChange={(event) => updateCheckInForm("comments", event.target.value)}
                placeholder="Optional notes about the resident's return"
                rows={3}
              />
            </label>
            <div className="assessment-drawer-actions resident-checkinout-confirm-actions">
              <button className="btn secondary" type="button" onClick={closeCheckInConfirmation} disabled={Boolean(checkingInId)}>
                Cancel
              </button>
              <button className="btn primary resident-checkinout-checkin-command" type="submit" disabled={Boolean(checkingInId)}>
                <i className={`ti ${checkingInId ? "ti-loader-2 spin" : "ti-clock-check"}`} />
                {checkingInId ? "Checking in..." : "Confirm Check In"}
              </button>
            </div>
          </form>
        ) : null}
      </Modal>

      <Drawer
        title="Check Out Resident"
        placement="right"
        open={drawerOpen}
        onClose={closeDrawer}
        width={760}
        className="resident-action-drawer"
      >
        <form className="assessment-drawer-form resident-clock-form resident-clockout-drawer-form" onSubmit={saveClockOut}>
          <section className="assessment-drawer-section resident-clockout-context">
            <div>
              <span>Resident CheckInOut</span>
              <strong>{selectedResident ? fullNameFor(selectedResident) : "Select resident"}</strong>
              <small>{selectedResident ? "Resident movement record" : "Choose the resident before selecting accompanying staff"}</small>
            </div>
            <i className="ti ti-clock-off" />
          </section>

          <section className="assessment-drawer-section resident-clockout-section">
            <h3>Resident</h3>
            <label className="ncp-form-field wide">
              <span>Resident</span>
              <Select
                className="resident-clockout-select resident-clockout-resident-select"
                value={form.residentId || undefined}
                showSearch
                placeholder="Search and select resident"
                optionFilterProp="label"
                optionLabelProp="label"
                popupMatchSelectWidth={false}
                dropdownStyle={{ minWidth: 360 }}
                onChange={(value) => setForm((current) => ({ ...current, residentId: value, staffIds: [] }))}
                options={residents.map((resident) => ({
                  value: residentIdFor(resident),
                  label: fullNameFor(resident),
                  title: fullNameFor(resident),
                }))}
              />
              {selectedResident ? (
                <span className="resident-clockout-selected-resident">
                  <i className="ti ti-user-check" />
                  <strong>{fullNameFor(selectedResident)}</strong>
                </span>
              ) : null}
              {selectedResidentOpenRecord ? (
                <span className="resident-clockout-open-warning">
                  <i className="ti ti-alert-triangle" />
                  <span>
                    This resident is already checked out since {formatDateTime(recordClockOutTime(selectedResidentOpenRecord))}. Check in before creating another check-out.
                  </span>
                </span>
              ) : null}
            </label>
          </section>

          <section className="assessment-drawer-section resident-clockout-section">
            <h3>Check-out Details</h3>
            <div className="assessment-drawer-grid">
              <label className="ncp-form-field wide">
                <span>Reason</span>
                <select value={form.reason} onChange={(event) => updateForm("reason", event.target.value)} required>
                  <option value="" disabled>Select reason</option>
                  {CLOCK_REASONS.map((reason) => <option key={reason} value={reason}>{reason}</option>)}
                </select>
              </label>
              {form.reason === "Other" ? (
                <label className="ncp-form-field wide">
                  <span>Specify reason</span>
                  <input value={form.otherReason} onChange={(event) => updateForm("otherReason", event.target.value)} placeholder="Reason for leaving" required />
                </label>
              ) : null}
              <label className="ncp-form-field">
                <span>Check-out time</span>
                <strong className="resident-checkinout-system-time">{formatDateTime(isoFromDateTimeLocal(form.clockOutTime))}</strong>
                <input type="hidden" value={form.clockOutTime} readOnly />
              </label>
              <label className="ncp-form-field">
                <span>Time expected back</span>
                <input type="datetime-local" value={form.expectedBackTime} onChange={(event) => updateForm("expectedBackTime", event.target.value)} />
              </label>
              <div className="ncp-form-field resident-checkinout-duration-field">
                <span>Expected time away</span>
                <strong>{durationBetween(isoFromDateTimeLocal(form.clockOutTime), isoFromDateTimeLocal(form.expectedBackTime))}</strong>
              </div>
              <label className="ncp-form-field wide">
                <span>Destination location address</span>
                {appConfigLoading && !googleMapsApiKey ? (
                  <input type="text" disabled placeholder="Loading map address search..." />
                ) : (
                  <GooglePlacesAutocompleteInput
                    googleMapsApiKey={googleMapsApiKey}
                    placeholder={googleMapsApiKey ? "Search destination address" : "Enter destination address"}
                    types={["address"]}
                    value={form.destinationLocationAddress}
                    onChange={(value) => updateForm("destinationLocationAddress", value)}
                    onResolvePlace={({ details, prediction }) => {
                      updateForm("destinationLocationAddress", details?.formatted_address || prediction?.description || "");
                    }}
                  />
                )}
              </label>
              <label className="ncp-form-field wide">
                <span>Emergency contact phone</span>
                <input type="tel" value={form.emergencyContactPhone} onChange={(event) => updateForm("emergencyContactPhone", event.target.value)} placeholder="Optional phone number for urgent contact" />
              </label>
              <label className="ncp-form-field wide">
                <span>Description</span>
                <textarea value={form.description} onChange={(event) => updateForm("description", event.target.value)} placeholder="Optional details about where the resident is going, appointment notes, transportation, or instructions." rows={3} />
              </label>
            </div>
          </section>

          <section className="assessment-drawer-section resident-clockout-section">
            <h3>Accompanying Staff</h3>
            <label className="ncp-form-field wide">
              <span>Search staff</span>
              <Select
                className="resident-clockout-select resident-clockout-staff-select"
                mode="multiple"
                value={form.staffIds}
                showSearch
                placeholder={form.residentId ? "Search accompanying staff" : "Select a resident first"}
                optionFilterProp="label"
                loading={staffLoading}
                disabled={!form.residentId || staffLoading}
                onChange={(values) => updateForm("staffIds", values)}
                options={staffChoices.map((staff) => ({
                  value: staff._clockStaffId,
                  label: [staffNameFor(staff), staffEmailFor(staff)].filter(Boolean).join(" - "),
                }))}
              />
            </label>
            {!staffLoading && form.residentId && !staffChoices.length ? <div className="resident-inline-empty">No assigned staff found for this resident.</div> : null}
            {!form.residentId ? <div className="resident-inline-empty">Select a resident to load accompanying staff.</div> : null}
            {selectedStaff.length ? (
              <div className="resident-clockout-selected-staff">
                {selectedStaff.map((staff) => (
                  <span key={staff._clockStaffId}>
                    <strong>{staffNameFor(staff)}</strong>
                    <small>{staffEmailFor(staff) || "No email added"}</small>
                  </span>
                ))}
              </div>
            ) : null}
          </section>
          <div className="assessment-drawer-actions">
            <button className="btn secondary" type="button" onClick={closeDrawer} disabled={saving}>Cancel</button>
            <button className="btn primary" type="submit" disabled={saving || Boolean(selectedResidentOpenRecord)}>
              <i className={`ti ${saving ? "ti-loader-2 spin" : "ti-clock-off"}`} /> {saving ? "Saving..." : "Check Out"}
            </button>
          </div>
        </form>
      </Drawer>
    </CareShell>
  );
}
